import React, { useCallback, useEffect, useState } from "react";
import Page from "./../../types/Page";
import Layout from "../../components/Layout";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useAuthUser } from "../../features/auth/authHooks";
import { selectInputErrors } from "../../features/errors/errorsSlice";
import {
  AccommodationQueryFilters,
  getAccommodationsAsync,
  selectAccommodationItems,
  selectAccommodationsLoadingStatus,
} from "../AccommodationsPage/accommodationsSlice";
import { DateRange } from "react-day-picker";
import InfoWebCoreApi from "../../common/infoweb/infoWebCoreApi";
import { PageData } from "../../types/InfoWebTypes";
import { InitialAllAccommodationsPaginationParameters } from "../../types/pagination.types";
import { LOADING_STATUS } from "../../common/constants";
import {
  ArrivalsAndDeparturesRequest,
  BookingType,
  DailyArrivalOrDepartureItem,
  getArrivalsAndDeparturesAsync,
  selectArrivalsAndDeparturesItem,
  selectArrivalsAndDeparturesItemsStatus,
} from "../BookingsPage/bookingsSlice";
import {
  bookingTypeEnumToTranslatedText,
  convertJSDateToDotNetDateTimeDayEnd,
  convertJSDateToDotNetDateTimeDayStart,
  convertJSDateToShortDateStringWithDayOfWeek,
} from "../../common/misc";
import { format } from "date-fns";
import LocalizedDatePicker from "../../components/DatePicker/LocalizedDatePicker";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { getUserClaimsAsync } from "../../features/auth/authSlice";
import { ICalEventModal } from "../../components/ICalEventModal";

const HomeCaretakersType: Page = {
  ActiveTab: "Home Caretakers",
  DisplayName: "Etusivu huoltajat",
  LinkUrl: "/home-caretakers",
  Path: "/home-caretakers",
  RouteName: "home-caretakers",
};

const HomeCaretakersPage = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const arrivalDateTimeFromParams = searchParams.get("arrivalDateTime");
  const departureDateTimeFromParams = searchParams.get("departureDateTime");
  const accommodationIdFromParam = searchParams.get("accommodationId");
  const { ownerAccountId } = useAuthUser();
  const { isOwnerUser } = useAuthUser();
  const { isLoggedIn } = useAuthUser();
  const inputErrors = useAppSelector(selectInputErrors);
  const accommodations = useAppSelector(selectAccommodationItems);
  const dailyArrivalsAndDeparturesItem = useAppSelector(
    selectArrivalsAndDeparturesItem,
  );

  const arrivalsAndDeparturesItemsLoadingStatus = useAppSelector(
    selectArrivalsAndDeparturesItemsStatus,
  );
  const accommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );

  // Real immutable 'today'
  const realToday = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0); // remove time part
    return d;
  }, []);
  // Get today's date
  let today = realToday;

  // Create a date object for tomorrow
  let tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  if (arrivalDateTimeFromParams != null) {
    today = new Date(arrivalDateTimeFromParams);
  }
  if (departureDateTimeFromParams != null) {
    tomorrow = new Date(departureDateTimeFromParams);
  }

  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>({
      from: today,
      to: tomorrow,
    });
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(tomorrow);
  const [pageData, setPageData] = useState<PageData>();
  const { loginFirstName, loginLastName } = useAuthUser();
  const [showAdditionalInformation, setShowAdditionalInformation] =
    useState<boolean>(true);
  const [
    accommodationDropdownSelectedAccommodation,
    setAccommodationDropdownSelectedAccommodation,
  ] = useState<string>(
    accommodationIdFromParam != null ? accommodationIdFromParam : undefined,
  );
  // Ensure correct account is selected before loading caretaker homepage data
  const [accountCheckDone, setAccountCheckDone] = useState(false);
  const [showICalModal, setShowICalModal] = useState(false);
  const [selectedICalBooking, setSelectedICalBooking] = useState<{
    bookingId?: string;
    adults?: number;
    children?: number;
    ownerBookingNotes?: string;
    startDateString?: string;
    endDateString?: string;
    checkInTime?: string;
    checkOutTime?: string;
    iCalDescription?: string;
    iCalExternalUid?: string;
    iCalFullDescription?: string;
    iCalLocation?: string;
    iCalImportId?: string;
    iCalDistributionChannelName?: string;
  } | null>(null);
  const [selectedICalAccommodationId, setSelectedICalAccommodationId] =
    useState<string>();

  const openICalModal = (
    booking: DailyArrivalOrDepartureItem,
    accommodationId: string,
  ) => {
    setSelectedICalBooking({
      bookingId: booking.bookingId,
      adults: booking.adults,
      children: booking.children,
      ownerBookingNotes: booking.ownerBookingNotes ?? "",
      startDateString: booking.startDateString ?? "",
      endDateString: booking.endDateString ?? "",
      checkInTime: booking.checkInTime ?? "",
      checkOutTime: booking.checkOutTime ?? "",
      iCalDescription: booking.iCalDescription,
      iCalExternalUid: booking.iCalExternalUid,
      iCalFullDescription: booking.iCalFullDescription,
      iCalLocation: booking.iCalLocation,
      iCalImportId: booking.iCalImportId,
      iCalDistributionChannelName: booking.iCalDistributionChannelName,
    });
    setSelectedICalAccommodationId(accommodationId);
    setShowICalModal(true);
  };

  useEffect(() => {
    const ensureAccountSelected = async () => {
      try {
        if (!isLoggedIn) return;
        const accounts: any[] = await apiService.get(
          API_ENDPOINTS.IDENTITY.GET_USER_ACCOUNTS,
        );
        if (!Array.isArray(accounts)) {
          setAccountCheckDone(true);
          return;
        }
        if (accounts.length > 1) {
          const selectionDone =
            localStorage.getItem("accountSelectionDone") === "true";
          if (selectionDone) {
            setAccountCheckDone(true);
            return;
          }
          if (window.location.pathname !== "/select-account") {
            window.location.replace("/select-account");
            return;
          }
        } else if (accounts.length === 1) {
          const selectionDone =
            localStorage.getItem("accountSelectionDone") === "true";
          if (selectionDone) {
            setAccountCheckDone(true);
            return;
          }
          const first = accounts[0];
          const id = first?.id ?? first?.accountId;
          if (id) {
            try {
              await apiService.post(
                API_ENDPOINTS.IDENTITY.SELECT_LOGIN_ACCOUNT,
                JSON.stringify(id),
              );
              await dispatch(getUserClaimsAsync()).unwrap();
              localStorage.setItem("accountSelectionDone", "true");
            } catch (e) {
              // ignore
            }
          }
          setAccountCheckDone(true);
        } else {
          setAccountCheckDone(true);
        }
      } catch (e) {
        setAccountCheckDone(true);
      }
    };
    ensureAccountSelected();
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    const fetchData = async () => {
      var pd = await InfoWebCoreApi.GetPageByPathAsync("fi/omaliittyma");

      if (pd != null) {
        setPageData(pd);
      }
    };

    fetchData().catch(console.error);
  }, []);

  const [filtering, setFiltering] = useState<AccommodationQueryFilters>({
    PageNumber: InitialAllAccommodationsPaginationParameters.PageNumber,
    PageSize: InitialAllAccommodationsPaginationParameters.PageSize,
    filterCriteria: {
      showDeleted: false,
      searchString: undefined,
      accommodationNumber: undefined,
      novelty: undefined,
      showInWebShop: undefined,
      showInOwnerPage: undefined,
      accommodationGroupId: undefined,
      accountId: ownerAccountId,
    },
  });
  const initAccommodationsFetch = useCallback(() => {
    dispatch(getAccommodationsAsync(filtering));
  }, [dispatch, ownerAccountId]);
  useEffect(() => {
    initAccommodationsFetch();
  }, [initAccommodationsFetch, ownerAccountId]);

  const accommodationDropdownChanged = (e: any) => {
    if (e.target.value != "0") {
      let selectedAccommodation = accommodations.find(
        (accommodation) => accommodation.id == e.target.value,
      );
      setAccommodationDropdownSelectedAccommodation(selectedAccommodation.id);
    } else {
      setAccommodationDropdownSelectedAccommodation(undefined);
    }
  };
  const setAccommodationDropdownDateRange = (
    startDateSelected: Date,
    endDateSelected: Date,
  ) => {
    let selectedRange = dayPickerSelectedRange;
    if (startDateSelected != undefined) {
      selectedRange.from = startDateSelected;
      setStartDate(startDateSelected);
      // Move enddate, if selected startdate is greater than endDate
      if (endDate < startDateSelected) {
        selectedRange.to = startDateSelected;
        setEndDate(startDateSelected);
      }
    }
    if (endDateSelected != undefined) {
      selectedRange.to = endDateSelected;
      setEndDate(endDateSelected);
    }
    setDayPickerSelectedRange(selectedRange);
  };

  const fetchDailyArrivalsAndDeparturesPossible = () => {
    return !(
      accommodations == undefined ||
      accommodations.length === 0 ||
      dayPickerSelectedRange == undefined ||
      dayPickerSelectedRange.from == undefined ||
      dayPickerSelectedRange.to == undefined
    );
  };
  const fetchDailyArrivalsAndDepartures = () => {
    const arrivalsAndDeparturesRequest: ArrivalsAndDeparturesRequest = {
      accommodationId:
        accommodationDropdownSelectedAccommodation != undefined
          ? accommodationDropdownSelectedAccommodation
          : "",
      ArrivalDateTime: convertJSDateToDotNetDateTimeDayStart(
        dayPickerSelectedRange.from,
      ),
      DepartureDateTime: convertJSDateToDotNetDateTimeDayEnd(
        dayPickerSelectedRange.to,
      ),
    };
    searchParams.set(
      "arrivalDateTime",
      format(dayPickerSelectedRange.from, "yyyy-MM-dd"),
    );
    searchParams.set(
      "departureDateTime",
      format(dayPickerSelectedRange.to, "yyyy-MM-dd"),
    );
    if (accommodationDropdownSelectedAccommodation != undefined) {
      searchParams.set(
        "accommodationId",
        accommodationDropdownSelectedAccommodation,
      );
    } else {
      searchParams.delete("accommodationId");
    }
    setSearchParams(searchParams);
    dispatch(getArrivalsAndDeparturesAsync(arrivalsAndDeparturesRequest));
  };

  // Block rendering until account selection (if needed) is handled
  if (!accountCheckDone) {
    return (
      <div className={HomeCaretakersType.RouteName}>
        <Layout CurrentPage={HomeCaretakersType}>
          <div className="d-flex gap-16 align-items-center">
            <Spinner animation="border" />
            <span>Tarkistetaan käyttäjätiliä…</span>
          </div>
        </Layout>
      </div>
    );
  }

  function renderTextWithLineBreaks(text: string): JSX.Element[] {
    return text.split(/<br\s*\/?>/i).map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split(/<br\s*\/?>/i).length - 1 && <br />}
      </React.Fragment>
    ));
  }
  if (
    accommodationsLoadingStatus == LOADING_STATUS.LOADING ||
    accommodations == null
  ) {
    return (
      <div className={HomeCaretakersType.RouteName}>
        <Layout CurrentPage={HomeCaretakersType}>
          <div className="d-grid gap-32">
            Ladataan kohteita...
            <Spinner animation="border" />
          </div>
        </Layout>
      </div>
    );
  }
  /*
  if (
    accommodationsLoadingStatus == LOADING_STATUS.IDLE &&
    accommodations.length == 0
  ) {
    //no accommodations
    //console.log("no accommodation");
    return <>Ei kohteita {accommodationsLoadingStatus}</>;
  }
   */
  let rowBookingNotesArray: string[] = [];

  return (
    <div className={HomeCaretakersType.RouteName}>
      <Layout CurrentPage={HomeCaretakersType}>
        <PageHeader
          Print={true}
          Back={false}
          Title="Tulijat ja lähtijät"
          className="border-bottom-0"
        />
        <Row className="d-print-none gy-16">
          <Col xl="4">
            <Form.Label htmlFor="selectCabin">Kohde</Form.Label>
            <Form.Select
              aria-label="Kohde"
              id="selectCabin"
              value={accommodationIdFromParam ?? 0} // controlled selection
              onChange={(e) => accommodationDropdownChanged(e)}
            >
              {accommodations.length > 1 && (
                <option value={0}>Näytä kaikki kohteet</option>
              )}
              {accommodations
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name)) // optional sorting
                .map((accommodation) => (
                  <option key={accommodation.id} value={accommodation.id}>
                    {accommodation.name}, {accommodation.accommodationNumber}
                  </option>
                ))}
            </Form.Select>
          </Col>
          <Col md="9" xl="5">
            <Form.Label htmlFor="selectStart">Valitse ajankohta</Form.Label>
            <div className="d-flex  gap-8 align-items-center">
              <div className="w-100 position-relative">
                <LocalizedDatePicker
                  selected={startDate}
                  onChange={(date) =>
                    setAccommodationDropdownDateRange(date, undefined)
                  }
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  className="form-control"
                  dateFormat={"dd.MM.yyyy"}
                />
                <Button
                  variant="link"
                  className="px-8 px-sm-16 position-absolute end-0 top-0 pe-none"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event"></span>
                </Button>
              </div>
              <span className="bi bi-arrow-right"></span>
              <div className="w-100 position-relative">
                <LocalizedDatePicker
                  selected={endDate}
                  onChange={(date) =>
                    setAccommodationDropdownDateRange(undefined, date)
                  }
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  className="form-control"
                  dateFormat={"dd.MM.yyyy"}
                />
                <Button
                  variant="link"
                  className="px-8 px-sm-16 position-absolute end-0 top-0 pe-none"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event"></span>
                </Button>
              </div>
            </div>
          </Col>

          <Col md="3" className="d-flex align-items-end">
            <Button
              disabled={!fetchDailyArrivalsAndDeparturesPossible()}
              onClick={() => fetchDailyArrivalsAndDepartures()}
              variant="primary"
              className="mt-auto w-100"
            >
              Hae
            </Button>
          </Col>
        </Row>
        <Row className="d-print-none gy-16">
          <Col lg="5">
            <br />
            <div className="form-check">
              <input
                checked={showAdditionalInformation}
                onChange={() =>
                  setShowAdditionalInformation(!showAdditionalInformation)
                }
                className="form-check-input"
                type="checkbox"
                value=""
                id="flexCheckDefault"
              />
              <Form.Label
                className="form-check-label"
                htmlFor="flexCheckDefault"
              >
                Varausten lisätiedot raportilla
              </Form.Label>
            </div>
          </Col>
        </Row>
        {arrivalsAndDeparturesItemsLoadingStatus != LOADING_STATUS.IDLE && (
          <div className="d-grid gap-32">
            Ladataan aikavälin tulijoita ja lähtijöitä...
            <Spinner animation="border" />
          </div>
        )}
        <table className="table bg-white mt-40">
          <thead>
            <tr>
              <th scope="col" style={{ width: "80px" }}>
                Pvm
              </th>
              <th scope="col">Kohde</th>
              <th scope="col">Tulijat</th>
              <th scope="col">Tulijat Lisävaraukset</th>
              <th scope="col">Lähtijät</th>
              <th scope="col">Lähtijät Lisävaraukset</th>
            </tr>
          </thead>
          {dailyArrivalsAndDeparturesItem != undefined && (
            <tbody>
              {dailyArrivalsAndDeparturesItem.dailyArrivalsAndDepartures !=
                undefined &&
                dailyArrivalsAndDeparturesItem.dailyArrivalsAndDepartures.map(
                  (item) => (
                    <React.Fragment
                      key={item.date + item.accommodationId + item.keyBox}
                    >
                      <tr
                        className={
                          rowBookingNotesArray.length > 0 ? "border-white" : ""
                        }
                      >
                        <th scope="row">
                          {convertJSDateToShortDateStringWithDayOfWeek(
                            new Date(item.date),
                          )}
                        </th>
                        <td>
                          <div className="d-grid">
                            {
                              <>
                                {
                                  <Link
                                    className="fw-bolder"
                                    to={
                                      "/accommodations/modify/" +
                                      item.accommodationId
                                    }
                                  >
                                    {item.accommodationNumber}{" "}
                                    {item.accommodationName}
                                  </Link>
                                }
                                <Link
                                  to={
                                    "/bookings/cabin/" +
                                    item.accommodationId +
                                    "?accommodationId=" +
                                    item.accommodationId +
                                    "&accommodationName=" +
                                    item.accommodationNumber +
                                    " - " +
                                    item.accommodationName
                                  }
                                  className="d-print-none"
                                >
                                  Varauskalenteri
                                </Link>
                                {/*}
                                <span>
                                  Asiakasarviot (
                                  {item.accommodationQualityRating})
                                </span>
                                {*/}
                                {item.keyBox && (
                                  <span>Avainkoodi: {item.keyBox}</span>
                                )}
                              </>
                            }
                          </div>
                        </td>
                        <td>
                          <div className="d-grid">
                            {item.arrivalItem != undefined && (
                              <>
                                <span>
                                  <Link
                                    to={
                                      "/bookings/booking/" +
                                      item.arrivalItem.bookingId +
                                      "?accommodationId=" +
                                      item.accommodationId
                                    }
                                    onClick={(e) => {
                                      if (
                                        item.arrivalItem.bookingTypeEnum ===
                                        BookingType.Ical
                                      ) {
                                        e.preventDefault();
                                        openICalModal(
                                          item.arrivalItem,
                                          item.accommodationId,
                                        );
                                      }
                                    }}
                                  >
                                    {item.arrivalItem.bookingTypeEnum !==
                                      BookingType.Ical && (
                                      <>
                                        <b>{item.arrivalItem.bookingNumber}</b>
                                        <br />
                                      </>
                                    )}
                                    {bookingTypeEnumToTranslatedText(
                                      item.arrivalItem.bookingTypeEnum,
                                    )}
                                  </Link>
                                </span>
                                <span>
                                  Tulo klo{" "}
                                  {item.arrivalItem.arrivalTime || "??"}
                                </span>
                                <span>
                                  {item.arrivalItem.customerFirstName}{" "}
                                  {item.arrivalItem.customerLastName}
                                </span>
                                {item.arrivalItem.customerPhoneNumber !=
                                  null && (
                                  <span>
                                    {item.arrivalItem.customerPhoneNumber}
                                  </span>
                                )}
                                <span>
                                  {item.arrivalItem.adults} +{" "}
                                  {item.arrivalItem.children}
                                </span>
                                <ul className="m-0 ps-16">
                                  {item.arrivalItem.dailyArrivalItemAddons !=
                                    undefined &&
                                    item.arrivalItem.dailyArrivalItemAddons
                                      .length > 0 &&
                                    item.arrivalItem.dailyArrivalItemAddons.map(
                                      (addon) => (
                                        <li
                                          key={
                                            item.date +
                                            item.accommodationId +
                                            item.keyBox +
                                            addon.units +
                                            addon.addonName
                                          }
                                        >
                                          {addon.units} x {addon.addonName}
                                        </li>
                                      ),
                                    )}
                                </ul>
                                {item.arrivalItem.bookingNotes != undefined &&
                                  item.arrivalItem.bookingNotes.trim() !==
                                    "" && (
                                    <span className="d-none">
                                      {rowBookingNotesArray.push(
                                        "Lisätieto varaus " +
                                          (item.arrivalItem.bookingTypeEnum ===
                                          BookingType.Ical
                                            ? "(iCal - tulo)"
                                            : item.arrivalItem.bookingNumber) +
                                          ": " +
                                          item.arrivalItem.bookingNotes,
                                      )}
                                    </span>
                                  )}
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-grid">
                            {item.arrivalItem != undefined &&
                              item.arrivalItem
                                .dailyArrivalOrDepartureItemExtraBookings !=
                                undefined &&
                              item.arrivalItem.dailyArrivalOrDepartureItemExtraBookings.map(
                                (extraBooking) => (
                                  <>
                                    Lisävaraus:{" "}
                                    {extraBooking.extraBookingNumber}
                                    <ul className="m-0 ps-16">
                                      {extraBooking.dailyArrivalOrDepartureItemAddons.map(
                                        (addon) => (
                                          <li
                                            key={
                                              item.date +
                                              item.accommodationId +
                                              item.keyBox +
                                              addon.units +
                                              addon.addonName
                                            }
                                          >
                                            {addon.units} x {addon.addonName}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                    {extraBooking.bookingNotes != undefined &&
                                      extraBooking.bookingNotes.trim() !==
                                        "" && (
                                        <span className="d-none">
                                          {rowBookingNotesArray.push(
                                            "Lisäv. " +
                                              extraBooking.extraBookingNumber +
                                              " lisätieto: " +
                                              extraBooking.bookingNotes,
                                          )}
                                        </span>
                                      )}
                                  </>
                                ),
                              )}
                          </div>
                        </td>
                        <td>
                          <div className="d-grid">
                            {item.departureItem != undefined && (
                              <>
                                <span>
                                  <Link
                                    to={
                                      "/bookings/booking/" +
                                      item.departureItem.bookingId +
                                      "?accommodationId=" +
                                      item.accommodationId
                                    }
                                    onClick={(e) => {
                                      if (
                                        item.departureItem.bookingTypeEnum ===
                                        BookingType.Ical
                                      ) {
                                        e.preventDefault();
                                        openICalModal(
                                          item.departureItem,
                                          item.accommodationId,
                                        );
                                      }
                                    }}
                                  >
                                    {item.departureItem.bookingTypeEnum !==
                                      BookingType.Ical && (
                                      <>
                                        <b>
                                          {item.departureItem.bookingNumber}
                                        </b>
                                        <br />
                                      </>
                                    )}
                                    {bookingTypeEnumToTranslatedText(
                                      item.departureItem.bookingTypeEnum,
                                    )}
                                  </Link>
                                </span>
                                <span>
                                  Lähtö klo{" "}
                                  {item.departureItem.departureTime || "??"}
                                </span>
                                <span>
                                  {item.departureItem.customerFirstName}{" "}
                                  {item.departureItem.customerLastName}
                                </span>
                                {item.departureItem.customerPhoneNumber !=
                                  null && (
                                  <span>
                                    {item.departureItem.customerPhoneNumber}
                                  </span>
                                )}
                                <span>
                                  {item.departureItem.adults} +{" "}
                                  {item.departureItem.children}
                                </span>
                                <ul className="m-0 ps-16">
                                  {item.departureItem.dailyArrivalItemAddons !=
                                    undefined &&
                                    item.departureItem.dailyArrivalItemAddons
                                      .length > 0 &&
                                    item.departureItem.dailyArrivalItemAddons.map(
                                      (addon) => (
                                        <li
                                          key={
                                            item.date +
                                            item.accommodationId +
                                            item.keyBox +
                                            addon.units +
                                            addon.addonName
                                          }
                                        >
                                          {addon.units} x {addon.addonName}
                                        </li>
                                      ),
                                    )}
                                </ul>
                                {item.departureItem.bookingNotes != undefined &&
                                  item.departureItem.bookingNotes.trim() !==
                                    "" && (
                                    <span className="d-none">
                                      {rowBookingNotesArray.push(
                                        "Lisätieto varaus " +
                                          (item.departureItem
                                            .bookingTypeEnum ===
                                          BookingType.Ical
                                            ? "(iCal - lähtö)"
                                            : item.departureItem
                                                .bookingNumber) +
                                          ": " +
                                          item.departureItem.bookingNotes,
                                      )}
                                    </span>
                                  )}
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-grid">
                            {item.departureItem != undefined &&
                              item.departureItem
                                .dailyArrivalOrDepartureItemExtraBookings !=
                                undefined &&
                              item.departureItem.dailyArrivalOrDepartureItemExtraBookings.map(
                                (extraBooking) => (
                                  <>
                                    Lisävaraus:{" "}
                                    {extraBooking.extraBookingNumber}
                                    <ul className="m-0 ps-16">
                                      {extraBooking.dailyArrivalOrDepartureItemAddons.map(
                                        (addon) => (
                                          <li
                                            key={
                                              item.date +
                                              item.accommodationId +
                                              item.keyBox +
                                              addon.units +
                                              addon.addonName
                                            }
                                          >
                                            {addon.units} x {addon.addonName}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                    {extraBooking.bookingNotes != undefined &&
                                      extraBooking.bookingNotes.trim() !==
                                        "" && (
                                        <span className="d-none">
                                          {rowBookingNotesArray.push(
                                            "Lisäv. " +
                                              extraBooking.extraBookingNumber +
                                              " lisätieto: " +
                                              extraBooking.bookingNotes,
                                          )}
                                        </span>
                                      )}
                                  </>
                                ),
                              )}
                          </div>
                        </td>
                      </tr>

                      {showAdditionalInformation &&
                        rowBookingNotesArray.length > 0 && (
                          <tr>
                            <td colSpan={6}>
                              {rowBookingNotesArray.map((note) => {
                                return (
                                  <p key={note}>
                                    {renderTextWithLineBreaks(note)}
                                  </p>
                                );
                              })}
                            </td>
                          </tr>
                        )}
                      {
                        <span className="d-none">
                          {(rowBookingNotesArray = [])}
                        </span>
                      }
                    </React.Fragment>
                  ),
                )}
            </tbody>
          )}
        </table>
        {/*}
        <Row>
          <Col>
            <div className="h-100 d-flex flex-column">
              <Heading Level={4} Title="Raportit" />
              <Card className="h-100">
                <Card.Body>
                  <Card.Text as="div">
                    <span className="bi bi-percent fs-1 text-primary"></span>
                    <h5>Arvonlisäveroerittely</h5>
                    <p>
                      Some quick example text to build on the card title and
                      make up the bulk of the card&apos;s content.
                    </p>
                    <Link className="btn w-100 btn-outline-primary" to={""}>
                      Siirry raporttiin
                    </Link>
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
          </Col>
          <Col>
            <div className="h-100 d-flex flex-column">
              <Heading Level={4} Title="Asiakaspalvelu" />
              <Card className="h-100">
                <Card.Body className="text-center">
                  <Card.Text className="p-16">
                    Arkisin ma-pe 8:30-17:00<br></br>
                    Lauantaisin 10:00-18:00
                  </Card.Text>

                  <div className="d-grid gap-16 mb-24">
                    <Link className="btn btn-primary" to={""}>
                      <span className="bi bi-phone me-8"></span>Soita 0306 502
                      502
                    </Link>
                    <Link className="btn btn-primary" to={""}>
                      <span className="bi bi-envelope  me-8"></span>
                      L&auml;het&auml; s&auml;hk&ouml;postia
                    </Link>
                  </div>
                  <Card.Text className="small">
                    P. 8,35 snt/puh + 3,2 snt/min.M. 19,2 snt/min.
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
        {*/}
        <ICalEventModal
          show={showICalModal}
          onHide={() => setShowICalModal(false)}
          eventData={selectedICalBooking}
          accommodationId={selectedICalAccommodationId}
          onSave={() => fetchDailyArrivalsAndDepartures()}
        />
      </Layout>
    </div>
  );
};

export default HomeCaretakersPage;
